import { Trans, useLingui } from '@lingui/react/macro';
import { Lock } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';

import { Pages } from 'modules/navigation/domain/enums/pages';
import { useFeature } from 'shared/hooks/use-feature';
import { theme } from 'styles/theme/theme';
import SparklesIcon from 'ud-ui/components/icon/sparkles-icon';
import UDText from 'ud-ui/components/text';
import UDTippy from 'ud-ui/components/tippy';

const AI_FEATURE = 'AI_CHAT_ASSISTANT';

/** Рамка — градиент из токенов, фон — белый: две подложки (`padding-box` под содержимым, `border-box` — рамка). */
const BORDER_GRADIENT = `linear-gradient(92deg, ${theme.colors.AiGradientViolet} 0%, ${theme.colors.AiGradientBlue} 52.5%, ${theme.colors.AiGradientYellow} 100%)`;
const BUTTON_BACKGROUND = `linear-gradient(${theme.colors.SurfaceCard}, ${theme.colors.SurfaceCard}) padding-box, ${BORDER_GRADIENT} border-box`;

/**
 * Кнопка шапки, ведущая в ИИ-ассистента (`/chat`).
 *
 * В покое — квадратная кнопка со значком (46×38), при наведении и фокусе плавно раскрывается и
 * показывает подпись «ИИ-ассистент»; уходит курсор — так же сворачивается обратно. Подпись — обычный
 * текст (`UDText` + `<Trans>`), поэтому следует языку и шрифту интерфейса, а ширина раскрытия — по
 * самой подписи, а не по числу: `grid-template-columns` анимируется от `0fr` к `1fr`, и кнопка
 * вырастает ровно на длину текста.
 *
 * Доступ — тот же, что у пункта меню «ИИ-ассистент»: без фичи тарифа кнопка помечена замком и ведёт
 * на вкладку «Подписка» с этой фичей (роут `/chat` закрыт ею же); пока фичи грузятся — не нажимается
 * и замка нет, потому что куда вести, ещё неизвестно.
 */
function AiChatButton() {
  const { t } = useLingui();
  const { isEnabled, isFetching } = useFeature(AI_FEATURE);

  const isLocked = !isFetching && !isEnabled;
  const label = t`ИИ-ассистент`;
  const paidLabel = t`Платный раздел`;
  const to = isEnabled ? Pages.CHAT.INDEX : Pages.SETTINGS.builders.subscription(AI_FEATURE);

  const link = (
    <Link
      to={to}
      aria-label={isLocked ? `${label}. ${paidLabel}` : label}
      aria-disabled={isFetching || undefined}
      tabIndex={isFetching ? -1 : undefined}
      onClick={(event) => {
        if (isFetching) event.preventDefault();
      }}
      style={{ background: BUTTON_BACKGROUND, border: '1px solid transparent' }}
      className="group rounded-card focus-visible:outline-accentNaplesYellow text-inkPrimary relative inline-flex h-[38px] shrink-0 items-center overflow-hidden px-3 focus-visible:outline-2 focus-visible:outline-offset-2 aria-disabled:pointer-events-none aria-disabled:opacity-60"
    >
      <SparklesIcon width={20} height={20} className="text-accentNaplesYellow shrink-0" aria-hidden="true" />

      {/* Ширина раскрытия — по тексту: колонка сетки 0fr → 1fr, а внутри обрезка по ширине. */}
      <span className="grid grid-cols-[0fr] transition-[grid-template-columns] duration-300 ease-out group-hover:grid-cols-[1fr] group-focus-visible:grid-cols-[1fr] motion-reduce:transition-none">
        <span className="min-w-0 overflow-hidden">
          <UDText
            variant="subtitle-1"
            as="span"
            className="block pl-2 whitespace-nowrap opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100 group-hover:delay-100 group-hover:duration-200 group-focus-visible:opacity-100 group-focus-visible:delay-100 group-focus-visible:duration-200 motion-reduce:transition-none"
          >
            <Trans>ИИ-ассистент</Trans>
          </UDText>
        </span>
      </span>

      {isLocked && <Lock size={10} aria-label={paidLabel} className="text-inkSecondary absolute top-[3px] right-[3px]" />}
    </Link>
  );

  if (!isLocked) return link;

  return (
    <UDTippy content={t`Доступно в платном тарифе — нажмите, чтобы подключить`} placement="bottom">
      {link}
    </UDTippy>
  );
}

export default AiChatButton;
